import type { CSSProperties, ElementType, ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../icons'
import styles from './Feedback.module.css'

export type CalloutTone = 'info' | 'warn' | 'success' | 'danger'

const CALLOUT_ICONS: Record<CalloutTone, IconName> = {
  info: 'info',
  warn: 'warning',
  success: 'check',
  danger: 'warning',
}

export interface CalloutProps {
  tone?: CalloutTone
  title?: ReactNode
  children?: ReactNode
  /** Extra controls (e.g. "Try again"). */
  action?: ReactNode
  /** 'alert' interrupts screen readers (errors); 'status' waits politely. */
  live?: 'alert' | 'status'
  icon?: IconName
  className?: string
}

/** A boxed message with an icon. Plain words, one next step. */
export function Callout({
  tone = 'info',
  title,
  children,
  action,
  live,
  icon,
  className,
}: CalloutProps) {
  return (
    <div className={cx(styles.callout, styles[tone], className)} role={live}>
      <Icon name={icon ?? CALLOUT_ICONS[tone]} size={22} className={styles.calloutIcon} />
      <div className={styles.calloutBody}>
        {title && <p className={styles.calloutTitle}>{title}</p>}
        {children && <div className={styles.calloutText}>{children}</div>}
        {action && <div className={styles.calloutAction}>{action}</div>}
      </div>
    </div>
  )
}

export interface EmptyStateProps {
  /** One spot illustration (decorative). */
  illustration?: ReactNode
  /** One plain sentence that says what is (not) here. */
  title: ReactNode
  /** An optional second plain sentence (what to do next). */
  description?: ReactNode
  /** Optional flavor line (≤1 per screen; never on errors). */
  flavor?: ReactNode
  /** One primary action. */
  action?: ReactNode
  headingLevel?: 'h1' | 'h2' | 'h3' | 'p'
  className?: string
}

/** Empty states: one illustration, one sentence, one action (06 §6.5). */
export function EmptyState({
  illustration,
  title,
  description,
  flavor,
  action,
  headingLevel = 'h2',
  className,
}: EmptyStateProps) {
  const Heading: ElementType = headingLevel
  return (
    <div className={cx(styles.empty, className)}>
      {illustration && (
        <div className={styles.illustration} aria-hidden="true">
          {illustration}
        </div>
      )}
      <Heading className={styles.emptyTitle}>{title}</Heading>
      {description && <p className={styles.description}>{description}</p>}
      {flavor && <p className={styles.flavor}>{flavor}</p>}
      {action && <div className={styles.emptyAction}>{action}</div>}
    </div>
  )
}

export interface SkeletonProps {
  width?: string
  height?: string
  radius?: string
  /** Render several text-line placeholders. */
  lines?: number
  className?: string
}

/** Still (never shimmering) placeholders in the line colour, for waits of 0.4–2 s. */
export function Skeleton({
  width = '100%',
  height = '1em',
  radius,
  lines,
  className,
}: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <span className={cx(styles.lines, className)} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <span
            key={i}
            className={styles.skeleton}
            style={{ width: i === lines - 1 ? '60%' : width, height, borderRadius: radius }}
          />
        ))}
      </span>
    )
  }
  const style: CSSProperties = { width, height, borderRadius: radius }
  return <span className={cx(styles.skeleton, className)} style={style} aria-hidden="true" />
}
